// Brand library API (middleware `/api/brand-assets`): the organization's
// logos, photos, graphics, icons, charts, fonts and colors — not tied to a
// brand profile. authedFetch, not apiFetch: a 403 here means "not an owner/admin",
// which must not trigger the token renewal + generic "Unauthorized" path
// apiFetch takes.

import { authedFetch } from "../../common/http_service";

const apiBase = () => (window.ideaLibraryRootUrl || "").replace(/\/$/, "");

async function readJson(response) {
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok) {
    const message = (body && body.message) || `Request failed (${response.status})`;
    const error = new Error(Array.isArray(message) ? message.join(", ") : message);
    error.status = response.status;
    throw error;
  }
  return body;
}

/**
 * The user's organization library: `{ assets: { [kind]: asset[] }, assetCounts: { [kind]: number },
 * categories: [{ id, name, assetCount }], categoryAssets: { [categoryId]: asset[] },
 * colors: [{ id, name, primary, secondary, accent }] }` — categories/categoryAssets are the
 * organization's own categories (kind `custom`); colors are its color sets (a slot is `#rrggbb` or null).
 */
export async function getBrandKit() {
  const res = await authedFetch(`${apiBase()}/api/brand-assets/kit`);
  return readJson(res);
}

const NO_PERMISSIONS = { canUpload: false, canManage: false };

/**
 * What may this user do with the brand library? Decided by the brand API
 * itself, so the UI follows the server rule — `canUpload` (today: every member
 * of the organization), `canManage` (owner/admin: e.g. choosing the main
 * logo). Any failure counts as "nothing" — those controls just stay hidden.
 */
export async function getBrandPermissions() {
  try {
    const res = await authedFetch(`${apiBase()}/api/brand-assets/permissions`, {}, { quiet: true });
    if (!res.ok) return NO_PERMISSIONS;
    const body = await res.json();
    return { canUpload: !!(body && body.canUpload), canManage: !!(body && body.canManage) };
  } catch {
    return NO_PERMISSIONS;
  }
}

/**
 * `isPrimary` makes the uploaded logo the organization's main logo (owner/admin only).
 * `categoryId` is required for kind `custom` — the organization category the file goes into.
 */
export async function uploadBrandAsset(kind, file, { isPrimary = false, categoryId = null } = {}) {
  const body = new FormData();
  body.append("file", file);
  body.append("kind", kind);
  if (isPrimary) body.append("isPrimary", "true");
  if (categoryId) body.append("categoryId", categoryId);
  const res = await authedFetch(`${apiBase()}/api/brand-assets`, {
    method: "POST",
    body,
  });
  return readJson(res);
}

/** Soft-deletes an asset (owner/admin only — the server answers 403 otherwise). */
export async function deleteBrandAsset(assetId) {
  const res = await authedFetch(`${apiBase()}/api/brand-assets/${encodeURIComponent(assetId)}`, {
    method: "DELETE",
  });
  return readJson(res);
}

/**
 * Adds a family from the editor's Google Fonts list to the library — no file;
 * the server stores Google's stylesheet as its url. A family already there
 * comes back as is. Open to every member, like an upload.
 */
export async function addGoogleFont(family) {
  const res = await authedFetch(`${apiBase()}/api/brand-assets/fonts/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ family }),
  });
  return readJson(res);
}

/**
 * Adds an empty brand color set (primary / secondary / accent) after the
 * organization's last one — open to every member, like an upload. Deleting a
 * set is deleteBrandAsset (owner/admin).
 */
export async function createBrandColorSet(name) {
  const res = await authedFetch(`${apiBase()}/api/brand-assets/colors`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(name ? { name } : {}),
  });
  return readJson(res);
}

/** Renames a color set (trimmed, 1–60 chars) — open to every member, like editing its colors. */
export async function renameBrandColorSet(setId, name) {
  const res = await authedFetch(
    `${apiBase()}/api/brand-assets/colors/${encodeURIComponent(setId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    }
  );
  return readJson(res);
}

/**
 * Fills or recolors (`#rrggbb`) one slot of a color set — `role` is primary /
 * secondary / accent — or empties it (`hex` null). Answers the whole set.
 */
export async function setBrandColor(setId, role, hex) {
  const res = await authedFetch(
    `${apiBase()}/api/brand-assets/colors/${encodeURIComponent(setId)}/${encodeURIComponent(role)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hex }),
    }
  );
  return readJson(res);
}

// Organization categories (owner/admin only — the server answers 403 otherwise).

const jsonRequest = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

/** A 409 means the organization already has a category with that name. */
export async function createBrandCategory(name) {
  const res = await authedFetch(
    `${apiBase()}/api/brand-assets/categories`,
    jsonRequest("POST", { name })
  );
  return readJson(res);
}

export async function renameBrandCategory(categoryId, name) {
  const res = await authedFetch(
    `${apiBase()}/api/brand-assets/categories/${encodeURIComponent(categoryId)}`,
    jsonRequest("PATCH", { name })
  );
  return readJson(res);
}

/** Soft-deletes the category and every asset in it. */
export async function deleteBrandCategory(categoryId) {
  const res = await authedFetch(
    `${apiBase()}/api/brand-assets/categories/${encodeURIComponent(categoryId)}`,
    { method: "DELETE" }
  );
  return readJson(res);
}
